// Shopify-compatible money formatting function
function formatMoney(cents, format = "${{amount}}") {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const defaultOption = (opt, def) => (typeof opt === 'undefined' ? def : opt);

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number === null) {
      return 0;
    }

    number = (number / 100).toFixed(precision);

    const [dollars, cents] = number.split('.');
    const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
    const formattedCents = cents ? decimal + cents : '';

    return formattedDollars + formattedCents;
  };

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholderRegex);

  if (!match) {
    return format;
  }

  const placeholder = match[1];
  let value;

  switch (placeholder) {
    case 'amount':
      value = formatWithDelimiters(cents);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = '';
  }

  return format.replace(placeholderRegex, value);
}

class QuizDynamicPricing {
  constructor(config) {
    this.config = config;
    this.selectedSellingPlan = config.selected_selling_plan;
    this.sellingPlans = config.selling_plans || [];
    this.bundleProduct = config.bundle_product;
    this.bundleVariant = config.bundle_variant;
    
    this.initRechargeBundle();
  }

  initRechargeBundle() {
    if (this.rechargeBundle) return;
    
    if (typeof window.recharge !== 'undefined' && window.recharge.bundle) {
      this.rechargeBundle = window.recharge.bundle;
    } else {
      this.rechargeBundle = null;
    }
  }

  calculateBasePrice(session) {
    let basePrice = 0;
    
    // Calculate recipe prices
    if (session.selected_recipes && session.selected_recipes.length > 0) {
      session.selected_recipes.forEach(recipe => {
        const product = window.QuizProductData?.Recipe?.find(r => r.id === recipe.product_id);
        if (product) {
          const price = parseFloat(product.price);
          basePrice += price * recipe.quantity;
        }
      });
    }
    
    // Calculate treats prices
    if (session.selected_treats && session.selected_treats.length > 0) {
      session.selected_treats.forEach(treat => {
        const product = window.QuizProductData?.Treats?.find(t => t.id === treat.id);
        if (product) {
          const price = parseFloat(product.price);
          basePrice += price * treat.quantity;
        }
      });
    }
    
    // Calculate supplements prices
    if (session.selected_supplements && session.selected_supplements.length > 0) {
      session.selected_supplements.forEach(supplement => {
        const product = window.QuizProductData?.Supplements?.find(s => s.id === supplement.id);
        if (product) {
          const price = parseFloat(product.price);
          basePrice += price * supplement.quantity;
        }
      });
    }
    
    return basePrice;
  }

  calculateSubscriptionPrice(basePrice, sellingPlanId = null, session = null) {
    const planId = sellingPlanId || this.selectedSellingPlan?.id;
    
    let sellingPlan = null;
    
    // First priority: use the selected selling plan object if IDs match (has complete price_adjustments)
    if (planId && this.selectedSellingPlan && this.selectedSellingPlan.id == planId) {
      sellingPlan = this.selectedSellingPlan;
    }
    
    // Second priority: try to find from bundle variant allocations
    if (!sellingPlan && planId && this.bundleVariant && this.bundleVariant.selling_plan_allocations && this.bundleVariant.selling_plan_allocations.length > 0) {
      const allocation = this.bundleVariant.selling_plan_allocations.find(alloc => 
        alloc && alloc.selling_plan && alloc.selling_plan.id == planId
      );
      if (allocation) {
        sellingPlan = allocation.selling_plan;
      }
    }
    
    // Third priority: fall back to config array
    if (!sellingPlan) {
      sellingPlan = this.sellingPlans.find(plan => plan.id == planId);
    }
    
    if (!sellingPlan) {
      return {
        price: basePrice,
        discount: 0,
        savings: 0,
        discountPercentage: 0
      };
    }

    // Extract discount percentage from the selling plan
    let discountPercentage = 0;
    
    // First try to get discount from price_adjustments array (from Shopify - most accurate)
    if (sellingPlan.price_adjustments && sellingPlan.price_adjustments.length > 0) {
      const priceAdjustment = sellingPlan.price_adjustments[0];
      if (priceAdjustment && priceAdjustment.value_type === 'percentage') {
        discountPercentage = priceAdjustment.value;
      }
    }
    // Fall back to discount_percentage field (from our config)
    else if (sellingPlan.discount_percentage) {
      discountPercentage = sellingPlan.discount_percentage;
    }
    
    // If we have session, apply discount per-item with currency-level rounding (all items eligible)
    if (session) {
      const getUnitPriceCents = (product) => {
        const raw = typeof product?.price === 'number' ? product.price : parseFloat(product?.price || 0);
        return Number.isNaN(raw) ? 0 : raw;
      };

      const computeDiscountedUnitCents = (unitCents) => {
        const dollars = unitCents / 100;
        const discountedDollars = dollars * (1 - discountPercentage / 100);
        const roundedDollars = Math.round(discountedDollars * 100) / 100;
        return Math.round(roundedDollars * 100);
      };

      let baseTotalCents = 0;
      let discountedTotalCents = 0;

      const process = (items, key) => {
        if (!items || items.length === 0) return;
        const all = window.QuizProductData?.[key] || [];
        items.forEach((item) => {
          const product = all.find((p) => p.id === (item.product_id || item.id));
          if (!product) return;
          const qty = item.quantity || 1;
          const unitCents = getUnitPriceCents(product);
          baseTotalCents += unitCents * qty;
          const discountedUnit = computeDiscountedUnitCents(unitCents);
          discountedTotalCents += discountedUnit * qty;
        });
      };

      process(session.selected_recipes, 'Recipe');
      process(session.selected_treats, 'Treats');
      process(session.selected_supplements, 'Supplements');

      // If nothing processed, fall back to base
      if (baseTotalCents === 0 && discountedTotalCents === 0) {
        baseTotalCents = basePrice;
        const eligibleDiscountCents = Math.round((basePrice / 100) * (discountPercentage / 100) * 100);
        discountedTotalCents = basePrice - eligibleDiscountCents;
      }

      const savings = Math.max(0, baseTotalCents - discountedTotalCents);

      return {
        price: discountedTotalCents,
        discount: savings,
        savings: savings,
        discountPercentage: discountPercentage
      };
    }

    // Otherwise apply to full base with currency-level rounding
    const eligibleDiscountCents = Math.round((basePrice / 100) * (discountPercentage / 100) * 100);
    const finalPrice = basePrice - eligibleDiscountCents;
    return {
      price: finalPrice,
      discount: eligibleDiscountCents,
      savings: eligibleDiscountCents,
      discountPercentage: discountPercentage
    };
  }  

  formatPrice(price) {
    const priceInCents = price;
    const moneyFormat = window.theme?.moneyFormat || window.shop?.money_format || window.quizInfo?.money_format || '${{amount}}';
    return formatMoney(priceInCents, moneyFormat);
  }

  formatDiscount(discount) {
    const discountFormatted = this.formatPrice(discount);
    return `Save ${discountFormatted}`;
  }

  formatDiscountPercentage(percentage) {
    return `${percentage}% OFF`;
  }

  updatePricing(session, sellingPlanId = null) {
    this.initRechargeBundle();
    
    const basePrice = this.calculateBasePrice(session);
    const subscriptionPricing = this.calculateSubscriptionPrice(basePrice, sellingPlanId, session);
    
    const result = {
      basePrice: basePrice,
      subscription: subscriptionPricing,
      formatted: {
        basePrice: this.formatPrice(basePrice),
        subscriptionPrice: this.formatPrice(subscriptionPricing.price),
        subscriptionSavings: this.formatDiscount(subscriptionPricing.savings),
        subscriptionDiscount: this.formatDiscountPercentage(subscriptionPricing.discountPercentage)
      }
    };
    
    return result;
  }

  async createRechargeBundle(session, sellingPlanId = null) {
    this.initRechargeBundle();
    if (!this.rechargeBundle) {
      throw new Error('Recharge bundle library not available');
    }

    if (!this.bundleProduct?.id || !this.bundleVariant?.id) {
      throw new Error('Bundle product or variant not properly configured');
    }

    const planId = sellingPlanId || this.selectedSellingPlan?.id;
    
    // First try to find in bundle variant allocations (most accurate data)
    let sellingPlan = null;
    if (this.bundleVariant?.selling_plan_allocations) {
      const allocation = this.bundleVariant.selling_plan_allocations.find(alloc => 
        alloc?.selling_plan?.id == planId
      );
      if (allocation) {
        sellingPlan = allocation.selling_plan;
      }
    }
    
    // Fallback to config selling plans if not found in allocations
    if (!sellingPlan) {
      sellingPlan = this.sellingPlans.find(plan => plan.id == planId);
    }
    
    if (!sellingPlan) {
      throw new Error(`Selling plan not found for ID: ${planId}`);
    }

    const selections = this.prepareBundleSelections(session, sellingPlan);
    
    if (!selections || selections.length === 0) {
      throw new Error('No bundle selections prepared');
    }

    const bundleData = {
      externalProductId: this.bundleProduct.id.toString(),
      externalVariantId: this.bundleVariant.id.toString(),
      selections: selections
    };

    // Validate the bundle before sending to Recharge
    const validation = await this.rechargeBundle.validateDynamicBundle(bundleData);
    if (validation !== true) {
      throw new Error(`Bundle validation failed: ${validation}`);
    }

    try {
      const bundleResponse = await this.rechargeBundle.getDynamicBundleItems(bundleData, this.bundleProduct.handle);
      return bundleResponse;
    } catch (error) {
      throw error;
    }
  }

  prepareBundleSelections(session, sellingPlan) {
    this.initRechargeBundle();
    const selections = [];
    
    // Get interval frequency and unit from selling plan
    let intervalFrequency = sellingPlan.delivery_policy?.interval_frequency;
    let intervalUnit = sellingPlan.delivery_policy?.interval_unit;
    
    // If no delivery_policy, extract from plan name
    if (!intervalFrequency || !intervalUnit) {
      const daysFromName = this.extractDaysFromPlanName(sellingPlan.name);
      if (daysFromName) {
        intervalFrequency = daysFromName;
        intervalUnit = 'day';
      } else {
        // Final fallback
        intervalFrequency = 1;
        intervalUnit = 'month';
      }
    }
    
    // Add selected recipes
    if (session.selected_recipes && session.selected_recipes.length > 0) {
      session.selected_recipes.forEach(recipe => {
        const product = window.QuizProductData?.Recipe?.find(r => r.id === recipe.product_id);
        if (product && recipe.variant_id && recipe.product_id) {
          const productSellingPlan = this.findMatchingSellingPlan(product, intervalFrequency, intervalUnit);
          if (productSellingPlan) {
            selections.push({
              collectionId: product.collections[0]?.id?.toString() || '',
              externalProductId: recipe.product_id.toString(),
              externalVariantId: recipe.variant_id.toString(),
              quantity: recipe.quantity || 1,
              sellingPlan: productSellingPlan.id,
              shippingIntervalFrequency: intervalFrequency,
              shippingIntervalUnitType: intervalUnit,
              discountedVariantId: recipe.variant_id
            });
          }
        }
      });
    }
    
    // Add selected treats
    if (session.selected_treats && session.selected_treats.length > 0) {
      session.selected_treats.forEach(treat => {
        const product = window.QuizProductData?.Treats?.find(t => t.id === treat.id);
        if (product && treat.id) {
          const variantId = treat.variant_id || product.first_available_variant?.id;
          const productSellingPlan = this.findMatchingSellingPlan(product, intervalFrequency, intervalUnit);
          if (variantId && productSellingPlan) {
            selections.push({
              collectionId: product.collections[0]?.id?.toString() || '',
              externalProductId: treat.id.toString(),
              externalVariantId: variantId.toString(),
              quantity: treat.quantity || 1,
              sellingPlan: productSellingPlan.id,
              shippingIntervalFrequency: intervalFrequency,
              shippingIntervalUnitType: intervalUnit,
              discountedVariantId: variantId
            });
          }
        }
      });
    }
    
    // Add selected supplements
    if (session.selected_supplements && session.selected_supplements.length > 0) {
      session.selected_supplements.forEach(supplement => {
        const product = window.QuizProductData?.Supplements?.find(s => s.id === supplement.id);
        if (product && supplement.id) {
          const variantId = supplement.variant_id || product.first_available_variant?.id;
          const productSellingPlan = this.findMatchingSellingPlan(product, intervalFrequency, intervalUnit);
          if (variantId && productSellingPlan) {
            selections.push({
              collectionId: product.collections[0]?.id?.toString() || '',
              externalProductId: supplement.id.toString(),
              externalVariantId: variantId.toString(),
              quantity: supplement.quantity || 1,
              sellingPlan: productSellingPlan.id,
              shippingIntervalFrequency: intervalFrequency,
              shippingIntervalUnitType: intervalUnit,
              discountedVariantId: variantId
            });
          }
        }
      });
    }
    
    return selections;
  }

  prepareBundleItems(session, sellingPlan) {
    const items = [];
    
    // Add selected recipes
    if (session.selected_recipes && session.selected_recipes.length > 0) {
      session.selected_recipes.forEach(recipe => {
        const product = window.QuizProductData?.Recipe?.find(r => r.id === recipe.product_id);
        if (product) {
          items.push({
            id: recipe.variant_id,
            quantity: recipe.quantity,
            selling_plan: sellingPlan.id,
            _rc_bundle_collection_id: product.collections[0]?.id || '',
            properties: {
              _rc_bundle_type: 'recipe',
              _rc_bundle_title: recipe.title
            }
          });
        }
      });
    }
    
    // Add selected treats
    if (session.selected_treats && session.selected_treats.length > 0) {
      session.selected_treats.forEach(treat => {
        const product = window.QuizProductData?.Treats?.find(t => t.id === treat.id);
        if (product) {
          items.push({
            id: treat.variant_id || product.first_available_variant?.id,
            quantity: treat.quantity,
            selling_plan: sellingPlan.id,
            _rc_bundle_collection_id: product.collections[0]?.id || '',
            properties: {
              _rc_bundle_type: 'treat',
              _rc_bundle_title: treat.title
            }
          });
        }
      });
    }
    
    // Add selected supplements
    if (session.selected_supplements && session.selected_supplements.length > 0) {
      session.selected_supplements.forEach(supplement => {
        const product = window.QuizProductData?.Supplements?.find(s => s.id === supplement.id);
        if (product) {
          items.push({
            id: supplement.variant_id || product.first_available_variant?.id,
            quantity: supplement.quantity,
            selling_plan: sellingPlan.id,
            _rc_bundle_collection_id: product.collections[0]?.id || '',
            properties: {
              _rc_bundle_type: 'supplement',
              _rc_bundle_title: supplement.title
            }
          });
        }
      });
    }
    
    return items;
  }

  findMatchingSellingPlan(product, targetIntervalFrequency, targetIntervalUnit) {
    if (!product?.selling_plan_groups || product.selling_plan_groups.length === 0) {
      return null;
    }

    // First try exact match by delivery_policy
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        for (const plan of group.selling_plans) {
          const planFrequency = plan.delivery_policy?.interval_frequency;
          const planUnit = plan.delivery_policy?.interval_unit;
          
          if (planFrequency == targetIntervalFrequency && planUnit === targetIntervalUnit) {
            return plan;
          }
        }
      }
    }

    // Second try: match by plan name patterns
    let targetDays;
    if (targetIntervalUnit === 'day' || targetIntervalUnit === 'days') {
      targetDays = targetIntervalFrequency;
    } else {
      targetDays = this.convertToTotalDays(targetIntervalFrequency, targetIntervalUnit);
    }
    
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        for (const plan of group.selling_plans) {
          const planDays = this.extractDaysFromPlanName(plan.name);
          
          if (planDays === targetDays) {
            return plan;
          }
        }
      }
    }

    // Fallback: return first available selling plan if no match
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        return group.selling_plans[0];
      }
    }

    return null;
  }

  convertToTotalDays(frequency, unit) {
    switch(unit) {
      case 'day':
      case 'days':
        return frequency;
      case 'week':
      case 'weeks':
        return frequency * 7;
      case 'month':
      case 'months':
        return frequency * 30; // approximation
      default:
        return frequency;
    }
  }

  extractDaysFromPlanName(planName) {
    if (!planName) return null;
    
    // Match patterns like "Every 7 days", "Every 21 days", "7 day", "21 day", "14 day subscription", etc.
    const dayMatch = planName.match(/(?:every\s+)?(\d+)\s*days?/i);
    if (dayMatch) {
      return parseInt(dayMatch[1]);
    }
    
    // Match "Every X weeks" patterns
    const weekMatch = planName.match(/(?:every\s+)?(\d+)\s*weeks?/i);
    if (weekMatch) {
      return parseInt(weekMatch[1]) * 7;
    }
    
    // Match "Monthly" or "Every month" patterns
    const monthMatch = planName.match(/(?:every\s+)?month(?:ly)?/i);
    if (monthMatch) {
      return 30;
    }
    
    return null;
  }

  generateBundleId() {
    return `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getSellingPlanDetails(sellingPlanId) {
    return this.sellingPlans.find(plan => plan.id == sellingPlanId);
  }

  updateSelectedSellingPlan(sellingPlanId) {
    let sellingPlan = null;
    
    // First priority: try to find from the bundle variant's selling plan allocations (has correct price_adjustments)
    if (this.bundleVariant && this.bundleVariant.selling_plan_allocations && this.bundleVariant.selling_plan_allocations.length > 0) {
      const allocation = this.bundleVariant.selling_plan_allocations.find(alloc => 
        alloc && alloc.selling_plan && alloc.selling_plan.id == sellingPlanId
      );
      if (allocation) {
        sellingPlan = allocation.selling_plan;
      }
    }
    
    // Second priority: try to find in our selling plans array
    if (!sellingPlan) {
      sellingPlan = this.sellingPlans.find(plan => plan.id == sellingPlanId);
    }
    
    if (sellingPlan) {
      this.selectedSellingPlan = sellingPlan;
    }
  }
}

// Export for use in other modules
window.QuizDynamicPricing = QuizDynamicPricing; 